// @flow
import * as React from 'react';
import {
  Button,
   Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem, Stack
} from '@mui/material';
import { forwardRef, useContext, useState } from 'react';
import { Circle, Pen } from '@phosphor-icons/react';
import { LoadingButton } from '@mui/lab';
import { useFormik } from 'formik';
import { FormikSelectInput } from '@coverright/ui/inputs';
import * as yup from 'yup';
import moment from 'moment';
import { ModalCloseButton } from '@coverright/ui/shared';
import { DisqualifiedReasonDto, StatusDto } from '@coverright/data-access/types/enrollment';
import { useSaveStatus } from './hooks/use-save-status';
import { AgentSelector } from '../agent-selector';
import { colorByStatus, labelByStatus } from '../../shared';
import { ContactedDateSelector, ExpectedCloseDateSelector, FollowUpSelector } from '../connect-client-date-selector';

interface Client {
  id: any,
  agent?: {
    id: any,
  } | null,
  status: StatusDto,
  followUpDate?: string,
  disqualifiedReason?: DisqualifiedReasonDto | null,
  expectedCloseDate?: string,
}

interface StatusFormProps {
  onSubmit: (value: boolean) => void;
  client: Partial<Client>
}

const schemaFields = {
  followUpDate: yup
    .string()
    .nullable(true)
    .test('followUpDate', 'Date is not valid', (value) => (
      !value ? true : moment(value).isValid()
    ))
    .typeError('Date is not valid'),
  expectedCloseDate: yup
    .string()
    .nullable(true)
    .test('expectedCloseDate', 'Date is not valid', (value) => (
      !value ? true : moment(value).isValid()
    ))
    .typeError('Date is not valid'),
  status: yup.string().required('Status is required'),
  agentId: yup.string().nullable(true),
  disqualifiedReason: yup.string().nullable(true),
}

export const StatusForm = forwardRef<HTMLFormElement, StatusFormProps>(({ onSubmit, client }, ref) => {
  const {save, loading} = useSaveStatus(() => onSubmit(true), client.id)

  const formik = useFormik({
    initialValues: {
      status: client.status,
      agentId: client.agent?.id,
      followUpDate: client.followUpDate,
      disqualifiedReason: client.disqualifiedReason,
      expectedCloseDate: client.expectedCloseDate,
    },
    validationSchema: yup.object(schemaFields),
    validateOnChange: true,
    onSubmit: values => {
      let error = false;
      if (formik.values.status && [StatusDto.FollowUp, StatusDto.Contacted, StatusDto.SalesQualified].includes(formik.values.status)) {
        if (!values.followUpDate) {
          formik.setFieldError('followUpDate', 'Date is required')
          error = true;
        }

        if ([StatusDto.Contacted, StatusDto.FollowUp, StatusDto.SalesQualified].includes(formik.values.status) && !values.agentId) {
          formik.setFieldError('agentId', 'Agent is required for this status')
          error = true;
        }

        if (StatusDto.SalesQualified === formik.values.status && !values.expectedCloseDate) {
          formik.setFieldError('expectedCloseDate', 'Date is required')
          error = true;
        }
      }

      if (StatusDto.Disqualified === formik.values.status && !values.disqualifiedReason) {
        formik.setFieldError('disqualifiedReason', 'Disqualified reason is required for this status')
        error = true;
      }

      if (!error) {
        save(values)
      }
    }
  });

  return <Stack component={'form'} ref={ref} onSubmit={formik.handleSubmit}>
    <AgentSelector
      value={formik.values.agentId}
      onChange={(agentId) => {
        formik.setFieldValue("agentId", agentId);
      }}
      error={formik.touched.agentId && Boolean(formik.errors.agentId) ? formik.errors.agentId as string : undefined}/>

    <FormikSelectInput formik={formik}
                       name={'status'}
                       wrapperSx={{mt: 2}}
                       hideTick
                       onChange={(event: any) => {
                         formik.setFieldValue("status", event.target.value as string);
                       }}
    >
      {statuses.map(s => (
        <MenuItem value={s} key={s}>
          <Stack gap={1} direction={'row'} alignItems={'center'}>
            <Circle size={16} weight={'fill'} color={colorByStatus(s)}/>
            {labelByStatus(s)}
          </Stack>
        </MenuItem>
      ))}
    </FormikSelectInput>
    <Collapse in={[StatusDto.FollowUp, StatusDto.SalesQualified].includes(formik.values.status as StatusDto)}
              sx={{mt: [StatusDto.FollowUp, StatusDto.SalesQualified].includes(formik.values.status as StatusDto) ? 2 : 0}} >
      <FollowUpSelector value={formik.values.followUpDate}
                        error={formik.touched.followUpDate ? formik.errors.followUpDate as string : undefined}
                        onChange={value => formik.setFieldValue("followUpDate", value)} />
    </Collapse>
    <Collapse sx={{mt: formik.values.status === StatusDto.Contacted ? 2 : 0}} in={formik.values.status === StatusDto.Contacted}>
      <ContactedDateSelector value={formik.values.followUpDate}
                             error={formik.touched.followUpDate ? formik.errors.followUpDate as string : undefined}
                             onChange={value => formik.setFieldValue("followUpDate", value)} />
    </Collapse>
    <Collapse sx={{mt: formik.values.status === StatusDto.SalesQualified ? 2 : 0}} in={formik.values.status === StatusDto.SalesQualified}>
      <ExpectedCloseDateSelector value={formik.values.expectedCloseDate}
                                 error={formik.touched.expectedCloseDate ? formik.errors.expectedCloseDate as string : undefined}
                                 onChange={value => formik.setFieldValue("expectedCloseDate", value)} />
    </Collapse>
    <Collapse sx={{mt: formik.values.status === StatusDto.Disqualified ? 2 : 0}} in={formik.values.status === StatusDto.Disqualified}>
      <FormikSelectInput label={'Select disqualified reason'}
                         formik={formik}
                         name={'disqualifiedReason'}
                   hideTick
                   value={formik.values.disqualifiedReason || ''}
                   onChange={(event: any) => {
                     formik.setFieldValue("disqualifiedReason", event.target.value as DisqualifiedReasonDto);
                   }}
      >
        <MenuItem value={DisqualifiedReasonDto.NoLongerInterestedInSwitching}>No Longer Interested In Switching</MenuItem>
        <MenuItem value={DisqualifiedReasonDto.UnableToConnect}> Unable To Connect</MenuItem>
        <MenuItem value={DisqualifiedReasonDto.WentToAnotherBroker}> Went To Another Broker</MenuItem>
      </FormikSelectInput>
    </Collapse>

    <Stack gap={2} direction={'row'} alignItems={'center'} justifyContent={'flex-end'} mt={2}>
      <Button onClick={() => onSubmit(false)} variant={'outlined'}>
        Cancel
      </Button>
      <LoadingButton
        loading={loading}
        type="submit"
        variant={'contained'}
      >
        Save
      </LoadingButton>
    </Stack>
  </Stack>
});

interface ContextProps {
  open: (client: Partial<Client>) => void;
}

const StatusFormContext = React.createContext<ContextProps>({
  open: () => {}
})

export const useStatusForm = () => useContext(StatusFormContext)

export function withStatusForm<P>(WrappedComponent: React.FC<P>) {
  return (props: P) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [client, setClient] = useState<Partial<Client>>();

    const open = (c: Partial<Client>) => {
      setClient(c)
      setDialogOpen(true)
    }

    const close = () => {
      setClient(undefined)
      setDialogOpen(false)
    }

    return <StatusFormContext.Provider value={{open}}>
      <Dialog fullWidth maxWidth={'xs'} open={dialogOpen}
              onClose={close}>
        <DialogTitle variant={'h3'}>
          Status
          <ModalCloseButton onClick={close}
                            sx={{position: 'absolute', top: 4, right: 8}} />
        </DialogTitle>
        <DialogContent>
          {client && <StatusForm client={client} onSubmit={close} />}
        </DialogContent>
      </Dialog>
      <WrappedComponent {...props} />
    </StatusFormContext.Provider>
  }
}

const statuses = [
  StatusDto.NotYetEngaged,
  StatusDto.Attempted,
  StatusDto.Contacted,
  StatusDto.FollowUp,
  StatusDto.SalesQualified,
  StatusDto.ApplicationSubmitted,
  StatusDto.Disenrolled,
  StatusDto.Disqualified,
  StatusDto.Test,
  StatusDto.Duplicated,
  StatusDto.OutOfService,
]
